import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Clock, Ellipsis, FolderPlus, Folder as FolderIcon, LayoutGrid, LogOut, Network, Plus, Search, Settings, Star, Trash2, Upload, Menu
} from "lucide-react";
import { PAGE_SIZES, type BoardMeta, type Folder, type PageSizeKey, type PaperKey, type PatternKey, type SepiaElement } from "../../shared/model";
import { Link, navigate } from "../app/router";
import { useSession } from "../app/session";
import { DEFAULT_NOTEBOOK, TEMPLATES, notebookPages, pageSize, previewFor, type NotebookSpec, type Template } from "../data/templates";
import { PAPERS } from "../editor/style";
import { api } from "../sync/api";
import * as local from "../sync/localdb";
import { Button, Chip, Dialog, Popover, Spinner, Tape, useToast } from "../ui/kit";
import { createGuestBoard } from "./guest";

type View = { kind: "all" } | { kind: "starred" } | { kind: "recent" } | { kind: "trash" } | { kind: "folder"; id: string } | { kind: "map" };

function ago(t: number): string {
  const s = (Date.now() - t) / 1000;
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} h ago`;
  if (s < 86400 * 7) return `${Math.floor(s / 86400)} d ago`;
  return new Date(t).toLocaleDateString(undefined, { month: "short", day: "numeric", year: s > 86400 * 300 ? "numeric" : undefined });
}

function greeting(): string {
  const h = new Date().getHours();
  return h < 5 ? "Burning the midnight oil" : h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening";
}

const CACHE = "sepia.library";

const RULINGS: { key: PatternKey; label: string }[] = [
  { key: "lines", label: "Ruled" },
  { key: "grid", label: "Quadrille" },
  { key: "dots", label: "Dots" },
  { key: "blank", label: "Plain" },
  { key: "iso", label: "Isometric" }
];

/** A small page drawn with the same ruling the real pages use. */
function PagePreview({ spec, pattern, height }: { spec: NotebookSpec; pattern: PatternKey; height: number }) {
  const { w, h } = pageSize(spec);
  const paper = PAPERS[spec.bg.paper] ?? PAPERS.cream;
  const k = height / h;
  const cell = Math.max(4, 24 * k * 1.6);
  const iso = `<svg xmlns='http://www.w3.org/2000/svg' width='${cell * Math.sqrt(3)}' height='${cell * 2}'><path d='M0,0L${cell * Math.sqrt(3)},${cell * 2}M0,${cell * 2}L${cell * Math.sqrt(3)},0M0,${cell}H${cell * Math.sqrt(3)}' stroke='${paper.major}' stroke-width='1' fill='none'/></svg>`;
  const vars = {
    width: Math.round(w * k), height,
    "--page-paper": paper.color, "--page-major": paper.major, "--page-line": paper.line,
    "--cell": cell + "px", "--rule": Math.max(5, 30 * k * 1.3) + "px", "--hair": "1px",
    "--iso-tile": `url("data:image/svg+xml,${encodeURIComponent(iso)}")`
  } as React.CSSProperties;
  return <span className={"page-sheet page-preview ruling-" + pattern} style={vars} aria-hidden="true" />;
}

function NotebookSetup({ spec, onChange, onCreate }: { spec: NotebookSpec; onChange: (s: NotebookSpec) => void; onCreate: () => void }) {
  return (
    <div className="notebook-setup">
      <div className="notebook-stage">
        <PagePreview spec={spec} pattern={spec.bg.pattern} height={170} />
      </div>
      <div className="notebook-options">
        <div className="form-row">
          <span className="form-label">page size</span>
          <div className="chips">
            {(Object.keys(PAGE_SIZES) as PageSizeKey[]).map((k) => (
              <Chip key={k} active={spec.size === k} onClick={() => onChange({ ...spec, size: k, landscape: k === "slide" ? true : spec.size === "slide" ? false : spec.landscape })}>
                {PAGE_SIZES[k].label}
              </Chip>
            ))}
          </div>
        </div>
        <div className="form-row">
          <span className="form-label">turned</span>
          <div className="chips">
            <Chip active={!spec.landscape} onClick={() => onChange({ ...spec, landscape: false })}>portrait</Chip>
            <Chip active={spec.landscape} onClick={() => onChange({ ...spec, landscape: true })}>landscape</Chip>
          </div>
        </div>
        <div className="form-row">
          <span className="form-label">ruling</span>
          <div className="ruling-picks" role="radiogroup" aria-label="Ruling">
            {RULINGS.map((r) => (
              <button key={r.key} type="button" role="radio" aria-checked={spec.bg.pattern === r.key} className={"ruling-pick" + (spec.bg.pattern === r.key ? " active" : "")} onClick={() => onChange({ ...spec, bg: { ...spec.bg, pattern: r.key } })}>
                <PagePreview spec={{ ...spec, landscape: false }} pattern={r.key} height={58} />
                <span>{r.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="form-row">
          <span className="form-label">paper</span>
          <div className="paper-swatches">
            {(Object.keys(PAPERS) as PaperKey[]).map((k) => (
              <button key={k} type="button" className={"paper-swatch" + (spec.bg.paper === k ? " active" : "")} style={{ background: PAPERS[k].color, color: PAPERS[k].ink }} onClick={() => onChange({ ...spec, bg: { ...spec.bg, paper: k } })}>
                {PAPERS[k].label}
              </button>
            ))}
          </div>
        </div>
        <div className="notebook-go">
          <Button kind="primary" onClick={onCreate}>Start the notebook</Button>
          <span className="muted small">You can add, reorder and remove pages any time.</span>
        </div>
      </div>
    </div>
  );
}

function loadNotebookSpec(): NotebookSpec {
  try {
    const s = JSON.parse(localStorage.getItem("sepia.notebook") || "null");
    if (s && PAGE_SIZES[s.size as PageSizeKey] && s.bg && PAPERS[s.bg.paper as PaperKey]) return { ...DEFAULT_NOTEBOOK, ...s, bg: { ...DEFAULT_NOTEBOOK.bg, ...s.bg } };
  } catch {
    /* ignore */
  }
  return DEFAULT_NOTEBOOK;
}

/** "Start a new board": an endless canvas (from a template) or a notebook of pages. */
export function NewBoardDialog({ open, onClose, onPick, onNotebook }: { open: boolean; onClose: () => void; onPick: (t: Template) => void; onNotebook: (spec: NotebookSpec) => void }) {
  const [kind, setKind] = useState<"canvas" | "pages">(() => (localStorage.getItem("sepia.newKind") === "pages" ? "pages" : "canvas"));
  const [spec, setSpec] = useState<NotebookSpec>(loadNotebookSpec);
  const choose = (k: "canvas" | "pages") => {
    setKind(k);
    localStorage.setItem("sepia.newKind", k);
  };
  return (
    <Dialog open={open} onClose={onClose} title="Start a new board" wide tape="var(--caramel)">
      <div className="kind-choice" role="radiogroup" aria-label="Kind of board">
        <button type="button" role="radio" aria-checked={kind === "canvas"} className={"kind-card" + (kind === "canvas" ? " active" : "")} onClick={() => choose("canvas")}>
          <img src="/art/templates/canvas.webp" alt="" width="80" height="80" />
          <span className="kind-text">
            <strong>Endless canvas</strong>
            <span>Unlimited room in every direction, for mind maps, diagrams and big pictures.</span>
          </span>
        </button>
        <button type="button" role="radio" aria-checked={kind === "pages"} className={"kind-card" + (kind === "pages" ? " active" : "")} onClick={() => choose("pages")}>
          <img src="/art/templates/notebook.webp" alt="" width="80" height="80" />
          <span className="kind-text">
            <strong>Pages</strong>
            <span>A notebook of paper pages. Write page by page and add more as you go.</span>
          </span>
        </button>
      </div>
      {kind === "canvas" ? (
        <div className="template-grid">
          {TEMPLATES.map((t) => (
            <button key={t.key} type="button" className={"template-card paper-" + t.bg.paper + " pattern-" + t.bg.pattern} onClick={() => onPick(t)}>
              <img className="template-icon" src={`/art/templates/${t.key}.webp`} alt="" loading="lazy" />
              <span className="template-title">{t.title}</span>
              <span className="template-blurb">{t.blurb}</span>
            </button>
          ))}
        </div>
      ) : (
        <NotebookSetup
          spec={spec}
          onChange={(s) => {
            setSpec(s);
            localStorage.setItem("sepia.notebook", JSON.stringify(s));
          }}
          onCreate={() => onNotebook(spec)}
        />
      )}
    </Dialog>
  );
}

function BoardCard({ b, folders, onChange, onDelete, guest }: { b: BoardMeta; folders: Folder[]; onChange: (patch: Record<string, unknown>) => void; onDelete: () => void; guest: boolean }) {
  const [menu, setMenu] = useState(false);
  const moreRef = useRef<HTMLButtonElement>(null);
  const rot = useMemo(() => ((parseInt(b.id.slice(-3), 36) % 7) - 3) * 0.35, [b.id]);
  const open = () => navigate((guest ? "/g/" : "/b/") + b.id);
  return (
    <div className={"board-card" + (b.trashedAt ? " trashed" : "")} style={{ ["--tilt" as string]: rot + "deg" }}>
      <button type="button" className="board-thumb" onClick={open} aria-label={"Open " + b.title}>
        {b.thumb ? <img src={b.thumb} alt="" loading="lazy" /> : <span className="thumb-empty">empty page</span>}
      </button>
      <Tape color={["#d98c8c", "#93ad86", "#d9b25a", "#8aa3bf", "#ab9ac7"][parseInt(b.id.slice(0, 2), 36) % 5]} className="card-tape" />
      <div className="board-meta">
        <button type="button" className="board-title-btn" onClick={open}>
          {b.title}
        </button>
        <span className="board-time">{ago(b.updatedAt)}</span>
      </div>
      <div className="board-actions">
        {!guest && !b.trashedAt && (
          <button type="button" className={"star-btn" + (b.starred ? " on" : "")} aria-label={b.starred ? "Unstar" : "Star"} onClick={() => onChange({ starred: !b.starred })}>
            <Star size={17} fill={b.starred ? "currentColor" : "none"} />
          </button>
        )}
        <button ref={moreRef} type="button" className="more-btn" aria-label="Board options" onClick={() => setMenu(true)}>
          <Ellipsis size={18} />
        </button>
      </div>
      <Popover anchor={moreRef.current} open={menu} onClose={() => setMenu(false)} align="end">
        <div className="menu" role="menu">
          {!b.trashedAt && (
            <>
              <button type="button" className="menu-item" onClick={() => { setMenu(false); open(); }}>Open</button>
              <button
                type="button"
                className="menu-item"
                onClick={() => {
                  setMenu(false);
                  const t = window.prompt("Rename board", b.title);
                  if (t && t.trim()) onChange({ title: t.trim() });
                }}
              >
                Rename
              </button>
              {!guest && <button type="button" className="menu-item" onClick={() => { setMenu(false); onChange({ duplicate: true }); }}>Duplicate</button>}
              {!guest && folders.length > 0 && (
                <>
                  <hr />
                  <span className="menu-label">Move to</span>
                  <button type="button" className="menu-item" onClick={() => { setMenu(false); onChange({ folderId: null }); }}>No folder</button>
                  {folders.map((f) => (
                    <button key={f.id} type="button" className="menu-item" onClick={() => { setMenu(false); onChange({ folderId: f.id }); }}>
                      {f.name}
                    </button>
                  ))}
                </>
              )}
              <hr />
              {guest ? (
                <button type="button" className="menu-item danger" onClick={() => { setMenu(false); if (window.confirm(`Delete “${b.title}” from this device?`)) onDelete(); }}>
                  Delete
                </button>
              ) : (
                <button type="button" className="menu-item danger" onClick={() => { setMenu(false); onChange({ trashed: true }); }}>
                  Move to trash
                </button>
              )}
            </>
          )}
          {b.trashedAt && (
            <>
              <button type="button" className="menu-item" onClick={() => { setMenu(false); onChange({ trashed: false }); }}>Restore</button>
              <button type="button" className="menu-item danger" onClick={() => { setMenu(false); if (window.confirm(`Delete “${b.title}” forever? This can't be undone.`)) onDelete(); }}>
                Delete forever
              </button>
            </>
          )}
        </div>
      </Popover>
    </div>
  );
}

function BoardMap({ onOpen }: { onOpen: (id: string) => void }) {
  const [data, setData] = useState<{ nodes: { id: string; title: string; size: number }[]; links: [string, string][] } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [pos, setPos] = useState<Map<string, { x: number; y: number }>>(new Map());
  useEffect(() => {
    api.graph().then(setData).catch((e) => setErr(e.message));
  }, []);
  // a small force-directed layout
  useEffect(() => {
    if (!data) return;
    const p = new Map(data.nodes.map((n, i) => [n.id, { x: Math.cos(i * 2.4) * 200 * Math.sqrt(i + 1) * 0.5, y: Math.sin(i * 2.4) * 200 * Math.sqrt(i + 1) * 0.5, vx: 0, vy: 0 }]));
    const ids = [...p.keys()];
    for (let it = 0; it < 300; it++) {
      for (let a = 0; a < ids.length; a++) {
        const pa = p.get(ids[a])!;
        for (let b = a + 1; b < ids.length; b++) {
          const pb = p.get(ids[b])!;
          const dx = pa.x - pb.x;
          const dy = pa.y - pb.y;
          const d2 = Math.max(100, dx * dx + dy * dy);
          const f = 9000 / d2;
          const d = Math.sqrt(d2);
          pa.vx += (dx / d) * f;
          pa.vy += (dy / d) * f;
          pb.vx -= (dx / d) * f;
          pb.vy -= (dy / d) * f;
        }
      }
      for (const [s, t] of data.links) {
        const ps = p.get(s);
        const pt = p.get(t);
        if (!ps || !pt) continue;
        const dx = pt.x - ps.x;
        const dy = pt.y - ps.y;
        ps.vx += dx * 0.01;
        ps.vy += dy * 0.01;
        pt.vx -= dx * 0.01;
        pt.vy -= dy * 0.01;
      }
      for (const v of p.values()) {
        v.vx -= v.x * 0.004;
        v.vy -= v.y * 0.004;
        v.x += v.vx * 0.5;
        v.y += v.vy * 0.5;
        v.vx *= 0.6;
        v.vy *= 0.6;
      }
    }
    setPos(new Map([...p.entries()].map(([k, v]) => [k, { x: v.x, y: v.y }])));
  }, [data]);
  if (err) return <p className="muted">{err}</p>;
  if (!data) return <Spinner />;
  if (!data.nodes.length) return <p className="muted">No boards yet.</p>;
  const xs = [...pos.values()].map((v) => v.x);
  const ys = [...pos.values()].map((v) => v.y);
  const minX = Math.min(...xs, 0) - 140;
  const minY = Math.min(...ys, 0) - 80;
  const w = Math.max(...xs, 0) - minX + 140;
  const h = Math.max(...ys, 0) - minY + 80;
  return (
    <div className="board-map">
      <p className="muted small">Each dot is a board. Lines are [[links]] you wrote in cards and notes.</p>
      <svg viewBox={`${minX} ${minY} ${w} ${h}`} role="img" aria-label="Map of linked boards">
        {data.links.map(([s, t], i) => {
          const a = pos.get(s);
          const b = pos.get(t);
          return a && b ? <line key={i} x1={a.x} y1={a.y} x2={b.x} y2={b.y} className="map-link" /> : null;
        })}
        {data.nodes.map((n) => {
          const p = pos.get(n.id);
          if (!p) return null;
          const r = 7 + Math.min(14, Math.sqrt(n.size));
          return (
            <g key={n.id} className="map-node" transform={`translate(${p.x} ${p.y})`} onClick={() => onOpen(n.id)}>
              <circle r={r} />
              <text y={r + 20} textAnchor="middle">
                {n.title.length > 26 ? n.title.slice(0, 25) + "…" : n.title}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}

export function Library() {
  const { user, signOut, offline } = useSession();
  const toast = useToast();
  const guest = !user;
  const [boards, setBoards] = useState<BoardMeta[] | null>(null);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [view, setView] = useState<View>({ kind: "all" });
  const [q, setQ] = useState("");
  const [results, setResults] = useState<{ id: string; title: string; snippet: string }[] | null>(null);
  const [picker, setPicker] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const importRef = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    if (guest) {
      const g = await local.listGuestBoards();
      setBoards(g.map((b) => ({ id: b.id, title: b.title, folderId: null, bg: b.bg, thumb: b.thumb, starred: false, trashedAt: null, shareToken: null, shareMode: null, links: [], elCount: b.elCount, createdAt: b.createdAt, updatedAt: b.updatedAt })));
      return;
    }
    try {
      const [b, f] = await Promise.all([api.boards(), api.folders()]);
      setBoards(b.boards);
      setFolders(f.folders);
      localStorage.setItem(CACHE, JSON.stringify({ boards: b.boards.map((x) => ({ ...x, thumb: x.thumb && x.thumb.length < 40000 ? x.thumb : null })), folders: f.folders }));
    } catch (e) {
      const cached = localStorage.getItem(CACHE);
      if (cached) {
        const c = JSON.parse(cached);
        setBoards(c.boards);
        setFolders(c.folders);
      } else {
        setBoards([]);
        toast((e as Error).message, "error");
      }
    }
  }, [guest, toast]);

  useEffect(() => {
    document.title = "Your boards · Sepia";
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  // full-text search on the server (titles + everything written on boards)
  useEffect(() => {
    if (guest || q.trim().length < 2) {
      setResults(null);
      return;
    }
    const t = window.setTimeout(() => api.search(q).then((r) => setResults(r.results)).catch(() => setResults(null)), 250);
    return () => window.clearTimeout(t);
  }, [q, guest]);

  const create = async (t: Template) => {
    setPicker(false);
    const title = t.key === "blank" ? "Untitled board" : t.title;
    try {
      if (guest) {
        const id = await createGuestBoard(title, t.key);
        navigate("/g/" + id);
        return;
      }
      const els: SepiaElement[] = t.make();
      const thumb = await previewFor(els, t.bg);
      const r = await api.createBoard({ title, bg: t.bg, els, thumb, folderId: view.kind === "folder" ? view.id : null });
      navigate("/b/" + r.board.id);
    } catch (e) {
      toast((e as Error).message, "error");
    }
  };

  const createNotebook = async (spec: NotebookSpec) => {
    setPicker(false);
    const title = "Untitled notebook";
    const els: SepiaElement[] = notebookPages(spec);
    try {
      if (guest) {
        const id = await createGuestBoard(title, "blank", { els, bg: spec.bg });
        navigate("/g/" + id);
        return;
      }
      const thumb = await previewFor(els, spec.bg);
      const r = await api.createBoard({ title, bg: spec.bg, els, thumb, folderId: view.kind === "folder" ? view.id : null });
      navigate("/b/" + r.board.id);
    } catch (e) {
      toast((e as Error).message, "error");
    }
  };

  const change = async (b: BoardMeta, patch: Record<string, unknown>) => {
    try {
      if (guest) {
        const g = await local.getGuestBoard(b.id);
        if (g && typeof patch.title === "string") await local.putGuestBoard({ ...g, title: patch.title });
      } else if (patch.duplicate) {
        await api.duplicateBoard(b.id);
        toast("Duplicated");
      } else await api.patchBoard(b.id, patch);
      load();
    } catch (e) {
      toast((e as Error).message, "error");
    }
  };

  const remove = async (b: BoardMeta) => {
    try {
      if (guest) await local.deleteGuestBoard(b.id);
      else await api.deleteBoard(b.id);
      load();
    } catch (e) {
      toast((e as Error).message, "error");
    }
  };

  const importFile = async (file: File) => {
    try {
      const data = JSON.parse(await file.text());
      if (!data || !Array.isArray(data.els)) throw new Error("That isn't a Sepia file.");
      const title = String(data.title || file.name.replace(/\.sepia\.json$|\.json$/i, "") || "Imported board");
      if (guest) {
        const id = await createGuestBoard(title, "blank");
        await local.writeEls("g:" + id, data.els, []);
        navigate("/g/" + id);
      } else {
        const r = await api.createBoard({ title, bg: data.bg, els: data.els });
        navigate("/b/" + r.board.id);
      }
    } catch (e) {
      toast((e as Error).message || "Couldn't import that file.", "error");
    }
  };

  const shown = useMemo(() => {
    if (!boards) return [];
    let list = boards;
    if (view.kind === "trash") return list.filter((b) => b.trashedAt);
    list = list.filter((b) => !b.trashedAt);
    if (view.kind === "starred") list = list.filter((b) => b.starred);
    if (view.kind === "recent") list = list.filter((b) => Date.now() - b.updatedAt < 7 * 86400_000);
    if (view.kind === "folder") list = list.filter((b) => b.folderId === view.id);
    if (q.trim() && (guest || !results)) list = list.filter((b) => b.title.toLowerCase().includes(q.trim().toLowerCase()));
    return list;
  }, [boards, view, q, results, guest]);

  const title =
    view.kind === "all" ? "All boards" : view.kind === "starred" ? "Starred" : view.kind === "recent" ? "This week" : view.kind === "trash" ? "Trash" : view.kind === "map" ? "Board map" : folders.find((f) => f.id === view.id)?.name ?? "Folder";

  const navItem = (v: View, label: string, Icon: typeof Star) => {
    const active = JSON.stringify(v) === JSON.stringify(view);
    return (
      <button type="button" className={"nav-item" + (active ? " active" : "")} onClick={() => { setView(v); setNavOpen(false); }}>
        <Icon size={18} strokeWidth={1.9} aria-hidden="true" />
        <span>{label}</span>
      </button>
    );
  };

  return (
    <div className={"library" + (navOpen ? " nav-open" : "")}>
      <aside className="shelf">
        <Link to="/welcome" className="brand">
          <img src="/favicon-64.png" alt="" width="34" height="34" />
          <span className="wordmark">sepia</span>
        </Link>
        <label className="search-box">
          <Search size={16} aria-hidden="true" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={guest ? "search titles…" : "search everything…"} />
        </label>
        <nav className="shelf-nav">
          {navItem({ kind: "all" }, "All boards", LayoutGrid)}
          {!guest && navItem({ kind: "starred" }, "Starred", Star)}
          {navItem({ kind: "recent" }, "This week", Clock)}
          {!guest && navItem({ kind: "map" }, "Board map", Network)}
          {!guest && navItem({ kind: "trash" }, "Trash", Trash2)}
        </nav>
        {!guest && (
          <div className="shelf-folders">
            <div className="shelf-head">
              <span>Folders</span>
              <button
                type="button"
                className="icon-btn small"
                aria-label="New folder"
                data-tip="New folder"
                onClick={async () => {
                  const name = window.prompt("Folder name");
                  if (!name?.trim()) return;
                  try {
                    await api.createFolder(name.trim());
                    load();
                  } catch (e) {
                    toast((e as Error).message, "error");
                  }
                }}
              >
                <FolderPlus size={17} />
              </button>
            </div>
            {folders.map((f) => (
              <div key={f.id} className="folder-row">
                {navItem({ kind: "folder", id: f.id }, f.name, FolderIcon)}
                <button
                  type="button"
                  className="folder-del"
                  aria-label={"Delete folder " + f.name}
                  onClick={async () => {
                    if (!window.confirm(`Delete the folder “${f.name}”? Its boards stay in All boards.`)) return;
                    await api.deleteFolder(f.id).catch(() => undefined);
                    if (view.kind === "folder" && view.id === f.id) setView({ kind: "all" });
                    load();
                  }}
                >
                  ×
                </button>
              </div>
            ))}
            {!folders.length && <p className="muted small">Group boards by course, project or mood.</p>}
          </div>
        )}
        <div className="shelf-foot">
          {user ? (
            <>
              <Link to="/account" className="nav-item">
                <Settings size={18} strokeWidth={1.9} aria-hidden="true" />
                <span>{user.name}</span>
              </Link>
              <button type="button" className="nav-item" onClick={() => signOut().then(() => navigate("/welcome"))}>
                <LogOut size={18} strokeWidth={1.9} aria-hidden="true" />
                <span>Log out</span>
              </button>
            </>
          ) : (
            <div className="guest-note">
              <p>Your boards are only on this device.</p>
              <Button kind="primary" onClick={() => navigate("/signup?import=1")}>Sign up to sync</Button>
              <Link to="/login" className="small">or log in</Link>
            </div>
          )}
          {offline && <p className="muted small">offline — showing what's saved here</p>}
        </div>
      </aside>

      <main className="shelf-main">
        <header className="shelf-top">
          <button type="button" className="icon-btn nav-toggle" aria-label="Menu" onClick={() => setNavOpen(!navOpen)}>
            <Menu size={20} />
          </button>
          <div>
            <p className="hello">{greeting()}{user ? `, ${user.name.split(" ")[0]}` : ""}</p>
            <h1>{title}</h1>
          </div>
          <div className="shelf-buttons">
            <button type="button" className="btn btn-plain" onClick={() => importRef.current?.click()}>
              <Upload size={16} aria-hidden="true" /> Import
            </button>
            <button type="button" className="btn btn-primary big" onClick={() => setPicker(true)}>
              <Plus size={18} strokeWidth={2.4} aria-hidden="true" /> New board
            </button>
          </div>
        </header>

        {view.kind === "map" ? (
          <BoardMap onOpen={(id) => navigate("/b/" + id)} />
        ) : results && q.trim() ? (
          <div className="search-results">
            {results.length ? (
              results.map((r) => (
                <button key={r.id} type="button" className="search-hit" onClick={() => navigate("/b/" + r.id)}>
                  <strong>{r.title}</strong>
                  {r.snippet && <span>{r.snippet}</span>}
                </button>
              ))
            ) : (
              <p className="muted">Nothing found for “{q}”.</p>
            )}
          </div>
        ) : !boards ? (
          <Spinner />
        ) : shown.length ? (
          <div className="board-grid">
            {view.kind === "all" && (
              <button type="button" className="board-card new-card" onClick={() => setPicker(true)}>
                <span className="plus">+</span>
                <span>new board</span>
              </button>
            )}
            {shown.map((b) => (
              <BoardCard key={b.id} b={b} folders={folders} guest={guest} onChange={(p) => change(b, p)} onDelete={() => remove(b)} />
            ))}
          </div>
        ) : (
          <div className="empty-shelf">
            <img src="/art/empty-library.webp" alt="" width="420" height="280" />
            <p>{view.kind === "trash" ? "The trash is empty." : view.kind === "starred" ? "Star a board to keep it close." : "A fresh sketchbook. What will you make?"}</p>
            {view.kind !== "trash" && view.kind !== "starred" && (
              <Button kind="primary" onClick={() => setPicker(true)}>
                Start a board
              </Button>
            )}
          </div>
        )}
      </main>
      <NewBoardDialog open={picker} onClose={() => setPicker(false)} onPick={create} onNotebook={createNotebook} />
      <input
        ref={importRef}
        type="file"
        accept=".json,application/json"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          e.target.value = "";
          if (f) importFile(f);
        }}
      />
    </div>
  );
}
